import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { Megaphone, ExternalLink, ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BannerItem {
  id: number;
  text: string;
  link?: string | null;
  isActive: boolean;
  order: number;
  createdAt: string;
}

interface NewsBannerProps {
  className?: string;
}

const NewsBanner: React.FC<NewsBannerProps> = ({ className = '' }) => {
  const [bannerItems, setBannerItems] = useState<BannerItem[]>([]);
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/banners`);
      // Focus on active banners, sorted by order
      const active = (response.data.banners || []).filter((b: BannerItem) => b.isActive);
      setBannerItems(active.sort((a: BannerItem, b: BannerItem) => a.order - b.order));
    } catch (error) {
      console.error('Error fetching banners:', error);
      setBannerItems([]);
    }
  };

  const handleBannerClick = (item: BannerItem) => {
    if (item.link) {
      if (item.link.startsWith('http')) {
        window.open(item.link, '_blank', 'noopener,noreferrer');
      } else {
        navigate(item.link);
      }
    } else {
      navigate('/announcement-details');
    }
  };

  if (bannerItems.length === 0) {
    return null;
  }

  const isRtl = document.documentElement.dir === 'rtl';

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {bannerItems.map((item) => (
        <div
          key={`announcement-${item.id}`}
          onClick={() => handleBannerClick(item)}
          className="group relative cursor-pointer overflow-hidden rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-primary-400 dark:bg-slate-900 dark:ring-slate-700/50 dark:hover:ring-primary-500"
        >
          {/* Subtle gradient accent */}
          <div className="absolute inset-y-0 start-0 w-1 bg-gradient-to-b from-blue-500 to-indigo-600 transition-all duration-300 group-hover:w-1.5" />
          
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 transition-colors duration-300 group-hover:bg-blue-600 group-hover:text-white dark:bg-blue-900/30 dark:text-blue-400">
              <Megaphone className="h-4 w-4" />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-snug text-slate-800 transition-colors duration-300 dark:text-slate-100 group-hover:text-primary-600 dark:group-hover:text-primary-400">
                {item.text}
              </p>
              
              <div className="mt-2 flex items-center justify-between">
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {item.link ? (
                    <>
                      <ExternalLink className="h-3 w-3" />
                      {t('card.viewDetails')}
                    </>
                  ) : (
                    <>
                      <Info className="h-3 w-3" />
                      {t('card.viewDetails')}
                    </>
                  )}
                </span>
                
                {isRtl ? (
                   <ChevronLeft className="h-4 w-4 text-slate-300 transition-transform duration-300 group-hover:-translate-x-1 group-hover:text-primary-500" />
                ) : (
                   <ArrowRight className="h-4 w-4 text-slate-300 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-primary-500" />
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Internal Import helper
const Info = ({ className }: { className?: string }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
);
const ArrowRight = ({ className }: { className?: string }) => (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
);

export default NewsBanner;